<template>
  <v-form
    ref="form"
    v-model="validForm"
    lazy-validation
  >
    <v-card
      max-width="700px"
      class="mx-auto"
      outlined
    >
      <v-card-title :class="$vuetify.breakpoint.mdAndDown ? 'pa-6' : 'pa-10'">
        <v-row class="justify-space-between align-center">
          <v-col>
            <h1 class="headline font-weight-medium">
              <span>{{ isEditing ? `Edit` : `New` }}</span>
              <span class="primary--text">&nbsp;{{ capitalize(editedPost.state) }}&nbsp;</span>
              <span>Update</span>
            </h1>
          </v-col>
          <v-col class="flex-grow-0 pa-0">
            <v-btn
              v-if="!isEditing"
              @click="closeModal"
              text
              fab
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-title>

      <v-card-text>
        <v-container :class="$vuetify.breakpoint.mdAndDown ? 'pa-0' : ''">
          <v-container class="grey--text">
            <span v-if="editedPost.createdAt" class="mr-6">
              <strong>Created:</strong> {{ formatTimestamp(editedPost.createdAt) }}
            </span>
            <span v-if="editedPost.updatedAt">
              <strong>Updated:</strong> {{ formatTimestamp(editedPost.updatedAt) }}
            </span>
            <p v-if="editedPost.author && user.isAdmin && isEditing" class="mt-2">
              <strong>Author:</strong> {{ editedPost.author.displayName }} (<a
                :href="'mailto:' + editedPost.author.email"
              >{{ editedPost.author.email }}</a>)
            </p>
          </v-container>

          <StatePickerMultiple
            @updateStates="updateStates"
            :selectedStates="editedPost.states"
            :includeNational="true"
          />

          <v-col cols="12">
            <v-text-field
              v-model="editedPost.headline"
              :disabed="!userCanEdit"
              :rules="[rules.required]"
              label="Headline"
              prepend-icon="mdi-format-title"
              class="headline"
              counter="60"
              maxlength="60"
            />

            <v-textarea
              v-model="editedPost.description"
              :disabed="!userCanEdit"
              label="Description"
              prepend-icon="mdi-subtitles-outline"
              auto-grow
              required
            />
            <v-text-field
              v-model="editedPost.hyperlinkPhase"
              :disabed="!userCanEdit"
              label="Hyperlink Phrase"
              prepend-icon="mdi-format-close-quote"
            />

            <br>

            <!-- <ImageUpload
              @setImgFields="setImgFields"
              @setImgUrl="setImgUrl"
              :disabed="!userCanEdit"
              :editedItem="editedPost"
              :imgUrl="editedPost.imgUrl"
              label="Post Image"
            /> -->

            <!-- <p class="headline red--text">
              To upload multiple images into a carousel, use the following field.
            </p> -->

            <ImageUploadMultiple
              v-show="!isImagesEditable"
              @setImages="setImages"
              @removeImage="removeImage"
              :disabed="!userCanEdit"
              :editedItem="editedPost"
              :existingImages="existingImages"
              label="Post Images"
            />

            <v-text-field
              v-show="!editedPost.images || (editedPost.images && editedPost.images.length === 0)"
              v-model="editedPost.videoLink"
              :disabed="!userCanEdit"
              label="Post Video Link"
              prepend-icon="mdi-link"
              clearable
            />

            <p class="red--text">
              Please do not enter Instagram links in the video link field.
            </p>

            <v-text-field
              v-model="editedPost.cta"
              :disabed="!userCanEdit"
              label="Call to Action Button Text"
              prepend-icon="mdi-bullhorn-outline"
              counter="10"
              maxlength="10"
            />

            <v-text-field
              v-model="editedPost.ctaUrl"
              :disabed="!userCanEdit"
              :rules="[rules.url]"
              label="Call to Action Button Link"
              prepend-icon="mdi-link"
              persistent-hint
              type="url"
            />
            <br>

            <v-select
              v-model="editedPost.postType"
              :disabed="!userCanEdit"
              :items="postTypes"
              :rules="[rules.required]"
              label="Post Type"
              prepend-icon="mdi-format-list-bulleted-type"
            />

            <br>

            <v-select
              v-model="editedPost.workflowState"
              v-if="user.isAdmin || user.isRegionalApprover"
              :disabed="!userCanEdit"
              :items="
                user.isAdmin || user.isRegionalApprover
                  ? Object.values(workflowStates)
                  : [workflowStates.draft, workflowStates['pending review']]
              "
              :rules="[rules.required]"
              label="Post Status"
              prepend-icon="mdi-progress-check"
            />

            <v-switch
              v-if="user.isAdmin"
              v-model="editedPost.isScheduled"
              :disabed="!userCanEdit"
              label="Publish this post for future release"
              prepend-icon="mdi-clock"
            />

            <div
              v-if="
                isEditing &&
                  editedPost.isScheduled &&
                  editedPost.scheduledNotificationDate &&
                  editedPost.scheduledNotificationTime
              "
            >
              <h3>
                THIS UPDATE IS CURRENTLY SCHEDULED FOR
                {{ editedPost.scheduledNotificationDate }} at
                {{ editedPost.scheduledTimeLabel }} ({{ editedPost.scheduledTimezoneLabel }})
              </h3>
            </div>

            <v-row v-if="editedPost.isScheduled" class="justify-space-between">
              <v-col>
                <v-date-picker
                  v-model="editedPost.scheduledPostDate"
                  :disabed="!userCanEdit"
                  :min="getEarliestAllowedScheduledPostDate()"
                />
              </v-col>

              <v-col>
                <v-select
                  v-model="editedPost.scheduledPostTime"
                  :items="scheduledPostTimes"
                  v-on:change="handleSelect"
                  :disabed="!userCanEdit"
                  item-text="label"
                  item-value="value"
                  label="Time of Day"
                />
              </v-col>
            </v-row>

            <RolesInputs
              @updateCategories="updateCategories"
              @updateRoles="updateRoles"
              @updateAffinityGroups="updateAffinityGroups"
              :disabed="!userCanEdit"
              :filterLabel="'Filter update by'"
              :editingCategories="editedPost.categories"
              :editingRoles="editedPost.roles"
              :editingAffinityGroups="editedPost.affinity_groups"
              :leaderToggle="true"
            />

            <v-row>
              <v-col>
                <v-switch
                  v-model="editedPost.volunteerTypeStudent"
                  :disabed="!userCanEdit"
                  label="Show to student volunteers ONLY"
                />
              </v-col>
              <v-col>
                <v-switch
                  v-model="editedPost.volunteerTypeSurvivor"
                  :disabed="!userCanEdit"
                  label="Show to survivor volunteers ONLY"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-switch
                  v-model="editedPost.isShowingToTesters"
                  :disabed="!userCanEdit"
                  label="Show only to Test group"
                />
              </v-col>
              <v-col>
                <v-switch
                  v-model="editedPost.isExcludingStudents"
                  :disabed="!userCanEdit"
                  label="Exclude student volunteers"
                />
              </v-col>
            </v-row>
          </v-col>

          <br><br>

          <v-card-actions>
            <v-spacer />
            <v-btn
              v-if="isEditing"
              @click="clearChanges"
              text
            >
              Clear Changes
            </v-btn>
            <v-btn
              v-if="!isEditing"
              @click="closeModal"
              color="primary darken-1"
              text
            >
              Cancel
            </v-btn>
            <v-btn
              @click="publish"
              color="primary"
              depressed
            >
              {{
                isEditing
                  ? 'Update'
                  : user.isAdmin || user.isRegionalApprover
                    ? 'Save'
                    : 'Save as Draft'
              }}
            </v-btn>
            <v-btn
              v-if="
                !user.isRegionalAdmin &&
                  user.isRegionalAdmin &&
                  editedPost.workflowState === 'draft'
              "
              @click="changeWorkflowState('pending review')"
              color="primary"
              depressed
            >
              Submit for Review
            </v-btn>
            <v-btn
              v-if="
                (user.isAdmin || user.isRegionalApprover) &&
                  isEditing &&
                  editedPost.workflowState === 'pending review'
              "
              @click="changeWorkflowState('draft')"
              color="primary darken-1"
              text
            >
              Return for Edits
            </v-btn>
          </v-card-actions>
        </v-container>
      </v-card-text>
    </v-card>
  </v-form>
</template>

<script>
import { mapState } from 'vuex';
import BaseForm from './BaseForm';
import { RolesInputs } from 'components';
import StatePickerMultiple from '../StatePickerMultiple';
import ImageUploadMultiple from './ImageUploadMultiple.vue';

export default {
  name: 'PostForm',

  components: {
    RolesInputs,
    //ImageUpload,
    ImageUploadMultiple,
    StatePickerMultiple,
  },

  extends: BaseForm,

  props: {
    isEditing: {
      type: Boolean,
      default: false,
    },
    editedPost: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      isShowingToTesters: false,
      existingImages: [],
      postTypes: ['Standard', 'Author Hidden'],
      workflowStates: {
        draft: {
          text: 'Draft',
          value: 'draft',
        },
        'pending review': {
          text: 'Pending review',
          value: 'pending review',
        },
        published: {
          text: 'Published',
          value: 'published',
        },
        archived: {
          text: 'Archived',
          value: 'archived',
        },
      },
      scheduledPostTimes: [
        {
          label: `12:00 AM (${this.handleTimezone()})`,
          value: '00:00',
        },
        // {
        //   label: `12:30 AM (${this.handleTimezone()})`,
        //   value: '00:30',
        // },
        {
          label: `1:00 AM (${this.handleTimezone()})`,
          value: '01:00',
        },
        // {
        //   label: `1:30 AM (${this.handleTimezone()})`,
        //   value: '01:30',
        // },
        {
          label: `2:00 AM (${this.handleTimezone()})`,
          value: '02:00',
        },
        // {
        //   label: `2:30 AM (${this.handleTimezone()})`,
        //   value: '02:30',
        // },
        {
          label: `3:00 AM (${this.handleTimezone()})`,
          value: '03:00',
        },
        // {
        //   label: `3:30 AM (${this.handleTimezone()})`,
        //   value: '03:30',
        // },
        { label: `4:00 AM (${this.handleTimezone()})`, value: '04:00' },
        // { label: `4:30 AM (${this.handleTimezone()})`, value: '04:30' },
        { label: `5:00 AM (${this.handleTimezone()})`, value: '05:00' },
        // { label: `5:30 AM (${this.handleTimezone()})`, value: '05:30' },
        { label: `6:00 AM (${this.handleTimezone()})`, value: '06:00' },
        // { label: `6:30 AM (${this.handleTimezone()})`, value: '06:30' },
        { label: `7:00 AM (${this.handleTimezone()})`, value: '07:00' },
        // { label: `7:30 AM (${this.handleTimezone()})`, value: '07:30' },
        { label: `8:00 AM (${this.handleTimezone()})`, value: '08:00' },
        // { label: `8:30 AM (${this.handleTimezone()})`, value: '08:30' },
        { label: `9:00 AM (${this.handleTimezone()})`, value: '09:00' },
        // { label: `9:30 AM (${this.handleTimezone()})`, value: '09:30' },
        { label: `10:00 AM (${this.handleTimezone()})`, value: '10:00' },
        // { label: `10:30 AM (${this.handleTimezone()})`, value: '10:30' },
        { label: `11:00 AM (${this.handleTimezone()})`, value: '11:00' },
        // { label: `11:30 AM (${this.handleTimezone()})`, value: '11:30' },
        { label: `12:00 PM (${this.handleTimezone()})`, value: '12:00' },
        // { label: `12:30 PM (${this.handleTimezone()})`, value: '12:30' },
        { label: `1:00 PM (${this.handleTimezone()})`, value: '13:00' },
        // { label: `1:30 PM (${this.handleTimezone()})`, value: '13:30' },
        { label: `2:00 PM (${this.handleTimezone()})`, value: '14:00' },
        // { label: `2:30 PM (${this.handleTimezone()})`, value: `14:30` },
        { label: `3:00 PM (${this.handleTimezone()})`, value: `15:00` },
        // { label: `3:30 PM (${this.handleTimezone()})`, value: `15:30` },
        { label: `4:00 PM (${this.handleTimezone()})`, value: `16:00` },
        // { label: `4:30 PM (${this.handleTimezone()})`, value: `16:30` },
        { label: `5:00 PM (${this.handleTimezone()})`, value: `17:00` },
        // { label: `5:30 PM (${this.handleTimezone()})`, value: `17:30` },
        { label: `6:00 PM (${this.handleTimezone()})`, value: `18:00` },
        // { label: `6:30 PM (${this.handleTimezone()})`, value: `18:30` },
        { label: `7:00 PM (${this.handleTimezone()})`, value: `19:00` },
        // { label: `7:30 PM (${this.handleTimezone()})`, value: `19:30` },
        { label: `8:00 PM (${this.handleTimezone()})`, value: `20:00` },
        // { label: `8:30 PM (${this.handleTimezone()})`, value: `20:30` },
        { label: `9:00 PM (${this.handleTimezone()})`, value: `21:00` },
        // { label: `9:30 PM (${this.handleTimezone()})`, value: `21:30` },
        { label: `10:00 PM (${this.handleTimezone()})`, value: `22:00` },
        // { label: `10:30 PM (${this.handleTimezone()})`, value: `22:30` },
        { label: `11:00 PM (${this.handleTimezone()})`, value: `23:00` },
        // { label: `11:30 PM (${this.handleTimezone()})`, value: `23:30` },
      ],
      currentlySelectedStates: [],
    };
  },

  computed: {
    ...mapState(['user']),

    schedulerValue: {
      get() {
        return this.editedPost.isScheduled;
      },
      set(newValue) {
        this.editedPost.isScheduled = newValue;
      },
    },
    isImagesEditable() {
      return this.editedPost.videoLink && this.editedPost.videoLink.length > 0;
    },
    userIsTester() {
      return this.user.isTester;
    },

    userCanEdit() {
      return !(!this.user.isAdmin && this.editedPost.workflowState !== 'draft');
    },
  },

  created() {
    this.existingImages = this.editedPost.images
      ? this.editedPost.images.map(imgData => JSON.parse(imgData))
      : [];
  },

  methods: {
    handleTimezone() {
      return new Date().toString().match(/\(([A-Za-z\s].*)\)/)[1];
    },
    handleSelect() {
      this.editedPost.scheduledTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    },
    updateCategories(categories) {
      this.editedPost.categories = categories;
    },

    updateRoles(roles) {
      this.editedPost.roles = roles;
    },

    updateAffinityGroups(affinity_groups) {
      this.editedPost.affinity_groups = affinity_groups;
    },

    updateStates(states) {
      this.editedPost.states = states;
      this.currentlySelectedStates = states;
    },

    publish() {
      if (this.invalidForm) {
        return;
      }
      this.$emit(this.isEditing ? 'update-post' : 'create-post');
    },

    changeWorkflowState(state) {
      if (this.invalidForm) {
        return;
      }

      //Change the workflow state to the value passed
      this.editedPost.workflowState = state;
      this.publish();
    },

    setImgFields(response) {
      this.editedPost.imgPublicId = response.public_id;
      this.editedPost.imgUrl = response.secure_url;
      this.editedPost.imgFormat = response.format;
      this.editedPost.imgWidth = response.width;
      this.editedPost.imgHeight = response.height;
    },
    setImgUrl(urlToEdit) {
      this.editedPost.imgUrl = urlToEdit;
    },
    setImages(imageArr) {
      this.editedPost.images = imageArr;
      this.existingImages = imageArr;
    },
    removeImage(index) {
      this.editedPost.images.splice(index, 1);
    },
    getEarliestAllowedScheduledPostDate() {
      const d = new Date();
      d.setTime(d.getTime() - 86400000);
      return d.toISOString();
    },
  },
};
</script>
